import { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { Button, Card, Descriptions, Flex, Typography } from 'antd';

import dayjs from 'dayjs';

import axios from '../../../axios';

import { hasPermission } from '../../../helpers/user';

import { DATE_FORMAT } from '../../../constants/common';
import { PERMISSIONS } from '../../../constants/permissions';
import { RULE_PRIORITY } from '../../../constants/rule';

import { fetchRule } from '../../../redux/actions/rule';

import AppModal from '../../../components/AppModal';
import PrevNextButtons from '../../../components/PrevNextButtons';
import Priority from '../../../components/PriorityTag';
import RuleSnoozeIcon from '../../../components/RuleSnoozeIcon';
import AppList from '../../../components/ui/AppList';

import AppBreadcrumb from '../../ui-components/AppBreadcrumb';
import AppDropdown from '../../ui-components/AppDropdown';

import RuleHistory from './RuleHistory';

const { Title } = Typography;

class Rule extends Component {
  constructor(props) {
    super(props);

    this.state = {
      actions: [],
      alert_modal: false,
      delete_modal: false,
      modal_message: '',
      modal_title: '',
      modal_type: '',
      rule: {}
    };

    this.archiveRule = this.archiveRule.bind(this);
    this.editRule = this.editRule.bind(this);
    this.generateDefinition = this.generateDefinition.bind(this);
    this.getRule = this.getRule.bind(this);
    this.initActions = this.initActions.bind(this);
    this.toggleModal = this.toggleModal.bind(this);
    this.getCurrentRule = this.props.getCurrentRule.bind(this);
  }

  async componentDidMount() {
    await this.getRule();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.rule?.id !== this.props?.rule?.id) {
      this.setState({ rule: this.props.rule }, () => this.generateDefinition());
    }
  }

  initActions(rule) {
    this.setState({
      actions: [
        {
          label: (
            <Button
              color="default"
              size="small"
              variant="link"
              disabled={rule.is_snoozed || !hasPermission([PERMISSIONS.RULES.UPDATE_OWN], this.props.user.permissions)}
              onClick={() => this.toggleModal('delete_modal', 'Snooze')}>
              Snooze rule
            </Button>
          )
        },
        {
          label: (
            <Button
              color="default"
              size="small"
              variant="link"
              disabled={!rule.is_snoozed || !hasPermission([PERMISSIONS.RULES.UPDATE_OWN], this.props.user.permissions)}
              onClick={() => this.toggleModal('delete_modal', 'Unsnooze')}>
              Unsnooze rule
            </Button>
          )
        },
        {
          label: (
            <Button
              color="default"
              size="small"
              variant="link"
              disabled={!hasPermission([PERMISSIONS.RULES.UPDATE_OWN], this.props.user.permissions)}
              onClick={() => this.editRule()}>
              Edit rule
            </Button>
          )
        },
        {
          label: (
            <Button
              color="default"
              size="small"
              variant="link"
              disabled={!hasPermission([PERMISSIONS.RULES.UPDATE_OWN], this.props.user.permissions)}
              onClick={() => this.archiveRule()}>
              Archive rule
            </Button>
          )
        }
      ]
    });
  }

  async getRule() {
    const rule = await this.getCurrentRule({ id: this.props.match.params.id, params: { query: this.state.query } });
    this.setState(prev => ({ ...prev, rule: rule.payload }));
    this.initActions(rule.payload);
  }

  editRule() {
    if (this.state.rule.default_rule === 1) {
      const modal = 'alert_modal';
      const type = modal;
      const title = 'Alert';
      const message = 'This is a default system Rule which can not be edited or archived.';

      this.setState(state => ({
        ...state,
        [modal]: !state[modal],
        modal_type: type,
        modal_title: title,
        modal_message: message
      }));
    } else {
      this.props.history.push('/rules/builder/' + this.props.match.params.id);
    }
  }

  toggleModal(modal, type) {
    let title = '';
    let message = '';

    if (type === 'Archive') {
      title = 'Archive rule';
      message = 'Are you sure you want to archive this rule? This action cannot be undone.';
    } else if (type === 'Snooze') {
      title = 'Snooze rule';
      message = 'Are you sure you want to snooze this rule?';
    } else if (type === 'Alert') {
      title = 'Alert';
      message = 'This is a default system Rule which can not be edited or archived.';
    } else {
      title = 'Unsnooze rule';
      message = 'Are you sure you want to unsnooze this rule?';
    }

    this.setState(state => ({
      ...state,
      [modal]: !state[modal],
      modal_type: type,
      modal_title: title,
      modal_message: message
    }));
  }

  generateDefinition() {
    const def = [];
    const data = this.state.rule.data;
    const type = this.state.rule.type.value || this.state.rule.type;
    const default_rule = this.state.rule.default_rule;
    const not_defined = <span>{'? '}</span>;

    if (type === 'intermediate_health') {
      def.push(<span>Any animal moves less than </span>);

      if (data.variables) {
        if (data.variables.percentage) {
          def.push(
            <span>
              <span className="bolded">{data.variables.percentage}%</span>
              {' of '}
            </span>
          );
        }

        if (data.variables.calculated_value) {
          def.push(
            <span>
              <span className="bolded">{data.variables.calculated_value.value}</span>{' '}
            </span>
          );
        }
      }

      if (data.action) {
        def.push(
          <span>
            {'. If rule breached '}
            {data.action.label}{' '}
          </span>
        );
      } else {
        def.push(not_defined);
      }

      this.setState({ rule: { ...this.state.rule, definition: def } });

      return;
    }

    if ((type === 'gateway_disconnected' || type === 'tag_disconnected') && default_rule === 1) {
      const selector = data.selector ? data.selector.label : '';
      const condition = data.condition ? data.condition.label : '';
      const action = data.action ? '. If rule breached ' + data.action.label : '';

      this.setState({ rule: { ...this.state.rule, definition: selector + ' ' + condition + action } });

      return;
    }

    if (data.selector) {
      def.push(
        <span>
          <span className="bolded">{data.selector.label}</span>
          {' livestock labeled '}
        </span>
      );
    } else {
      def.push(not_defined);
    }

    if (data.anchor) {
      def.push(
        <span>
          <span className="bolded">{data.anchor.label}</span>{' '}
        </span>
      );
    } else {
      def.push(not_defined);
    }

    if (data.condition) {
      def.push(<span>{data.condition.label} </span>);
    } else {
      def.push(not_defined);
    }

    if (type === 'site_proximity' || type === 'livestock_proximity' || type === 'basic_health') {
      if (data.variables) {
        if (data.variables.distance) {
          def.push(
            <span>
              <span className="bolded">{data.variables.distance} </span>
            </span>
          );
        } else {
          def.push(not_defined);
        }

        if (data.variables.distance_unit) {
          def.push(
            <span>
              <span className="bolded">
                {data.variables.distance_unit.label} {type !== 'basic_health' && ' of '}
              </span>
            </span>
          );
        } else {
          def.push(not_defined);
        }

        if (type === 'basic_health') {
          if (data.variables.timeframe) {
            def.push(
              <span>
                <span className="bolded">{data.variables.timeframe.label}</span>
              </span>
            );
          } else {
            def.push(not_defined);
          }
        }
      }
    }

    if (data.target) {
      def.push(
        <span>
          <span className="bolded">{data.target.label} </span>
        </span>
      );
    }

    if (type === 'site_frequency') {
      if (data.variables) {
        if (data.variables.frequency) {
          def.push(
            <span>
              <span className="bolded">{data.variables.frequency} </span> {' times '}{' '}
            </span>
          );
        } else {
          def.push(not_defined);
        }

        if (data.variables.timeframe) {
          def.push(
            <span>
              <span className="bolded">{data.variables.timeframe.label}</span>
            </span>
          );
        } else {
          def.push(not_defined);
        }
      }
    }

    if (data.action) {
      def.push(
        <span>
          {'. If rule breached '}
          {data.action.label}{' '}
        </span>
      );
    } else {
      def.push(not_defined);
    }

    this.setState({ rule: { ...this.state.rule, definition: def } });
  }

  archiveRule() {
    if (this.state.rule.default_rule === 1) {
      this.toggleModal('alert_modal', 'Alert');
    } else {
      this.toggleModal('delete_modal', 'Archive');
    }
  }

  async deleteRule() {
    let response = {};

    if (this.state.modal_type === 'Archive') {
      response = await axios.delete('rules/' + this.props.match.params.id, {
        data: { force: true }
      });
    } else if (this.state.modal_type === 'Snooze') {
      response = await axios.put(`rules/${this.props.match.params.id}/snooze`, {
        seconds: 86400
      });
    } else {
      response = await axios.put(`rules/${this.props.match.params.id}/snooze`, {
        seconds: -86400
      });
    }

    if (response.status && response.status === 200) {
      this.props.history.push('/rules');
    }
  }

  render() {
    if (this.state.rule.id === null) return null;

    const data = [
      {
        label: 'Rule ID',
        value: `${this.state.rule.identifier} ${this.state.rule.is_snoozed ? <RuleSnoozeIcon /> : ''}`,
        isVisible: !!this.state.rule.identifier
      },
      {
        label: 'Rule Name',
        value: this.state.rule.name,
        isVisible: !!this.state.rule.name
      },
      {
        label: 'Rule Snoozed Until',
        value: dayjs(this.state.rule.unsnooze_at).format(DATE_FORMAT.DATETIME),
        isVisible: !!this.state.rule.is_snoozed
      },
      {
        label: 'Rule Type',
        value: this.state.rule.type_display_name,
        isVisible: !!this.state.rule.type_display_name
      },
      {
        label: 'Rule Priority',
        value: <Priority priority={RULE_PRIORITY[this.state.rule?.data?.action?.value]} />,
        isVisible: !!(this.state.rule?.data && this.state.rule?.data?.action)
      },
      {
        label: 'Rule Status',
        value: this.state.rule.is_snoozed ? 'Snoozed' : 'Active',
        isVisible: true
      },
      {
        label: 'Rule Created',
        value: dayjs(this.state.rule.created_at).format(DATE_FORMAT.DATETIME),
        isVisible: true
      },
      {
        label: 'Rule Definition',
        value: this.state.rule.definition,
        isVisible: true
      },
      {
        label: 'Rule Action',
        value: this.state.rule?.data?.action?.label,
        isVisible: !!this.state.rule?.data?.action?.label
      },
      {
        label: 'Rule Objects',
        value: `${this.state.rule.objects_count} (Livestock: ${this.state.rule.animals_count}, Sites:
              ${this.state.rule.sites_count}, Pastures: ${this.state.rule.geofences_count}, Gateways:
              ${this.state.rule.gateway_count}, Tags: ${this.state.rule.tag_count})`,
        isVisible: !!this.state.rule?.data?.action?.label
      },
      {
        label: 'Rule Triggers',
        value: this.state.rule.triggers_count,
        isVisible: true
      },
      {
        label: 'User Actions',
        value: (
          <>
            Snoozed on
            {this.state.rule.snooze_history &&
              this.state.rule.snooze_history.map(x => dayjs(x.created_at).format(DATE_FORMAT.DATETIME)).join(', ')}
            <br />
          </>
        ),
        isVisible: true
      },
      {
        label: 'Time-based',
        value: this.state.rule.timeBased ? 'Yes' : 'No',
        isVisible: true
      },
      {
        label: 'Rule Description',
        value: this.state.rule.description,
        isVisible: true
      },
      {
        label: 'Linked groups',
        value: this.state.rule?.labels?.map((item, index) => {
          let link = '';
          if (index > 0) {
            link = (
              <>
                ,{' '}
                <Link key={item.id} to={`/group/${item.id}`}>
                  {item.identifier}
                </Link>
              </>
            );
          } else
            link = (
              <Link key={item.id} to={`/group/${item.id}`}>
                {item.identifier}
              </Link>
            );
          return link;
        }),
        isVisible: true
      }
    ];

    const breadcrumbItems = [
      {
        title: <Link to="/rules">List of Rules</Link>
      },
      {
        title: `${this.state.rule?.name ? `Rule ${this.state.rule.name}` : 'Rule'}`
      }
    ];

    return (
      <div key={this.props.match.params.id}>
        <Flex gap="small" vertical={false} justify="space-between" wrap>
          <Flex vertical={true}>
            <Title level={4}>Rule «{this.state.rule.name}»</Title>

            <AppBreadcrumb items={breadcrumbItems} />
          </Flex>
          <Flex gap="small">
            {this.state.actions?.length > 0 && <AppDropdown label="Actions" items={this.state.actions} />}

            <PrevNextButtons
              nextId={this.props?.rule?.next_id}
              prevId={this.props?.rule?.prev_id}
              handlePrev={() => this.props.history.push(`/rule/${this.props?.rule?.prev_id}`)}
              handleNext={() => this.props.history.push(`/rule/${this.props?.rule?.next_id}`)}
            />
          </Flex>
        </Flex>

        <Card className="mb-2" title={`Rule ${this.state.rule.name || this.state.rule.identifier} details`}>
          <AppList>
            {data?.map(d => (
              <>
                {d.isVisible && (
                  <Descriptions.Item key={d.label} label={d.label}>
                    {d.value}
                  </Descriptions.Item>
                )}
              </>
            ))}
          </AppList>
        </Card>

        <RuleHistory ruleId={this.props.match.params.id} />

        <AppModal
          isOpen={this.state.delete_modal}
          confirmButtonColor="danger"
          confirmButtonText={this.state.modal_type}
          title={this.state.modal_title}
          handleCancel={() => this.toggleModal('delete_modal')}
          handleConfirm={() => this.deleteRule()}>
          <div className="py-4">{this.state.modal_message}</div>
        </AppModal>

        <AppModal
          isOpen={this.state.alert_modal}
          cancelButtonText="Close"
          confirmButtonHidden={true}
          title={this.state.modal_title}
          handleCancel={() => this.toggleModal('alert_modal')}>
          <div className="py-4">{this.state.modal_message}</div>
        </AppModal>
      </div>
    );
  }
}

export default connect(
  state => ({
    rule: state.rule.rule,
    user: state.user
  }),
  dispatch => ({
    getCurrentRule: ({ id, params }) => dispatch(fetchRule({ id, params }))
  })
)(Rule);
